import MuiTabular from 'modules/common/components/mui-tabular';

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { Card, IconButton, Typography, Grid } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useDispatch, useSelector } from 'react-redux';
import CollapseIcon from 'modules/common/components/collapse-icon';
import {
  selectHotelName,
  selectPmsSync,
  selectResetBookingDate,
  selectTargetDate,
  selectWorkbookDateType,
} from 'modules/dashboard/selectors';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import { ENVIRONMENT } from 'config';
import { AssistantIcon } from 'modules/common/components';
import { useDebouncedCallback } from 'use-debounce';
import { BREAKDOWN_COLUMN_INFO_MAP, COLUMNS, PACE_TABLE_COLUMNS } from '../../../../constants';
import {
  buildPaceFilters,
  buildPacePlanHeaderData,
  handleCeruleanLogging,
  updateMuiPaceColumnLabels,
} from '../../../../function';
import {
  CARD_CONTENT_STYLE,
  EXPORT_PDF_BUTTON_STYLE,
  exportPdfTextStyle,
  HEADER_STYLE,
  ROW_STYLE,
  TABULAR_CARD_STYLE,
} from '../../../../style';
import ColumnInfo from '../column-info/column-info';
import { selectPaceData, selectWorkbookBookingDate } from '../../../../selectors';
import { workbookActions } from '../../../../slice';

const MuiPaceVisual = ({ isDisplay, onClick, title, enableSecondaryHotelGroups = false }) => {
  const bookingDate = useSelector(selectWorkbookBookingDate);
  const pms = useSelector(selectPmsSync);
  const targetDate = useSelector(selectTargetDate);
  const selectedDateType = useSelector(selectWorkbookDateType);
  const organizationId = useSelector(selectOrganizationId);
  const hotels = useSelector(selectHotelName);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const resetBookingDate = useSelector(selectResetBookingDate);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  //
  const paceData = useSelector(selectPaceData);
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState(PACE_TABLE_COLUMNS);
  const dispatch = useDispatch();
  //
  const tableRef = useRef();
  //
  const fetchData = useDebouncedCallback(async () => {
    let start;
    // Build the filters for the API request
    const filters = buildPaceFilters({
      bookingDate,
      targetDate,
      pms,
      hotels,
      arrival: selectedDateType?.id === 'ARRIVAL' ? 'arrival_date' : 'stay_date',
    });
    try {
      setIsLoading(true);
      start = performance.now();
      // Log filter payload
      handleCeruleanLogging({
        message: `Room Nights Pace By Channel Monthly Breakdown Filters: ${JSON.stringify(
          filters
        )}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      // Log request start
      handleCeruleanLogging({
        message: `Room Nights Pace By Channel Monthly Breakdown Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      setExportData({
        columns: COLUMNS,
        filters,
        title: 'mui-pace-visual',
      });
      dispatch(
        workbookActions.getPaceData({
          organizationId,
          columns: COLUMNS,
          filters,
        })
      );
    } catch (error) {
      handleCeruleanLogging({
        message: `Error fetching Room Nights Pace By Channel Monthly Breakdown tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      const end = performance.now();

      handleCeruleanLogging({
        message: `Room Nights Pace By Channel Monthly Breakdown  Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });

      handleCeruleanLogging({
        message: `Room Nights Pace By Channel Monthly Breakdown  Tabular Request time: ${(
          (end - start) /
          1000
        ).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    if (!paceData?.length) return;
    const columnData = paceData[paceData.length - 1];
    const updatedColumns = updateMuiPaceColumnLabels(PACE_TABLE_COLUMNS, columnData);
    setColumns(updatedColumns);
  }, [paceData]);
  //
  useEffect(() => {
    if (!isDisplay) {
      return;
    }
    fetchData();
  }, [isDisplay, selectedDateType, pms, targetDate, bookingDate, hotels]);
  //
  useEffect(() => {
    if (paceData && isLoading) {
      setIsLoading(false);
    }
  }, [paceData]);
  //
  const headerData = useMemo(
    () =>
      buildPacePlanHeaderData({
        bookingDate,
        targetDate,
        pms,
        hotels,
        selectedDateType,
        resetBookingDate,
      }),
    [bookingDate, targetDate, selectedDateType, pms, hotels, resetBookingDate]
  );

  return (
    <>
      <Grid mb={2} container item direction="row" justifyContent="flex-end" alignItems="center" />
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        className={styles.mainGrid}
        mb={10}
      >
        <Card sx={TABULAR_CARD_STYLE}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            height={40}
          >
            <Grid item pl={2}>
              {title}
            </Grid>
            <Grid item>
              <Grid item container direction="row" justifyContent="flex-end" alignItems="center">
                {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                  <Grid item mt={1} mr={4}>
                    <AssistantIcon
                      exportData={exportData}
                      visualName={title}
                      isProcessing={isExporting}
                      setIsProcessing={setIsExporting}
                      disabled={isProcessing || isLoading || isExporting || !isDisplay}
                      isCerulean
                      enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                      getExcelExportData={tableRef?.current?.getExcelData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  </Grid>
                )}
                <>
                  <Typography sx={exportPdfTextStyle(isLoading)}>Export Data</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsProcessing(true)}
                    disabled={isLoading}
                    sx={EXPORT_PDF_BUTTON_STYLE}
                  >
                    <GetAppIcon />
                  </IconButton>
                </>
                <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
              </Grid>
            </Grid>
          </Grid>
          {isDisplay && (
            <Grid sx={CARD_CONTENT_STYLE}>
              {!paceData ? (
                <Grid container justifyContent="center" alignItems="center" m={5}>
                  <Typography fontWeight={500}>Loading Data...</Typography>
                </Grid>
              ) : (
                <>
                  <MuiTabular
                    ref={tableRef}
                    columns={columns}
                    data={paceData?.length ? paceData?.slice(0, paceData.length - 1) : []}
                    isLoadingData={isLoading}
                    isAggregate={false}
                    fileName="Room_Nights_Pace_Channel_Monthly"
                    headerFiltersInfo={headerData}
                    customHeaderStyle={HEADER_STYLE}
                    customRowStyle={ROW_STYLE}
                    isProcessing={isProcessing}
                    setIsProcessing={setIsProcessing}
                    headerComponent={
                      <Grid container spacing={2} mt={1} pl={1} mb={1}>
                        {headerData?.sections.map((section, index) => (
                          <Grid item xs={12} sm={6} md={4} key={`${section?.id}-${index.toString()}`}>
                            <Typography
                              fontSize={10.5}
                              fontFamily="'Segoe UI', wf_segoe-ui_normal, helvetica, arial, sans-serif"
                            >
                              {section?.title}: {section?.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    }
                  />
                  <ColumnInfo columnInfoMap={BREAKDOWN_COLUMN_INFO_MAP} />
                </>
              )}
            </Grid>
          )}
        </Card>
      </Grid>
    </>
  );
};

export default MuiPaceVisual;
